import { FONT_MONO } from '../../config/fonts';
import { useEffect, useState } from "react";
import { Box, Slider, Typography, alpha } from "@mui/material";
import { HexColorPicker } from "react-colorful";
import { Label, Palette, Radar as RadarIcon, Timeline } from "@mui/icons-material";

import { useAuthContext } from "../../context/auth";
import { useUnitContext } from "../../context/unit";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { ROLES } from "../../config/roles";
import { ShowNumberModes, SpeedUnits } from "../../config/types";
// mapUtils' rgbToHex takes (r, g, b). MUI's rgbToHex takes an "rgb(...)" string and throws on
// anything else, which crashed the Scan color picker.
import { hexToRgbString, rgbToHex } from "../../utils/mapUtils";
import {
  SegmentedSetting,
  SidebarPanel,
  SidebarSection,
  SliderSetting,
  SwatchSetting,
  SwitchSetting,
  accentOf,
} from "./SidebarKit";

const FILTER_FIELDS = [
  { key: "hue", label: "Hue", min: -180, max: 360, unit: "°" },
  { key: "brightness", label: "Brightness", min: 50, max: 150, unit: "%" },
  { key: "contrast", label: "Contrast", min: 50, max: 150, unit: "%" },
  { key: "saturate", label: "Saturation", min: 0, max: 7000, unit: "%" },
];

const FilterSliders = ({ value, onChange, accent }) => (
  <Box sx={{ width: 220 }}>
    {FILTER_FIELDS.map(({ key, label, min, max, unit }) => (
      <Box key={key} sx={{ mb: 0.5 }}>
        <Box display="flex" justifyContent="space-between" sx={{ fontSize: "0.72rem" }}>
          <span style={{ color: "rgba(255,255,255,0.8)" }}>{label}</span>
          <span style={{ color: accent, fontFamily: FONT_MONO, fontWeight: 700 }}>
            {value[key]}{unit}
          </span>
        </Box>
        <Slider
          size="small"
          value={value[key]}
          min={min}
          max={max}
          onChange={(e, v) => onChange({ ...value, [key]: v })}
          sx={{ mt: -0.5, color: accent, "& .MuiSlider-thumb": { width: 12, height: 12 } }}
        />
      </Box>
    ))}
  </Box>
);

const filterCss = (f) => `
  brightness(0)
  saturate(100%)
  invert(${f.invert}%)
  sepia(${f.sepia}%)
  saturate(${f.saturate}%)
  hue-rotate(${f.hue}deg)
  brightness(${f.brightness}%)
  contrast(${f.contrast}%)
`;

// The preview is the real plane marker with the filter applied, so it looks exactly like the map
const PlanePreview = ({ filter, size = 22 }) => (
  <Box sx={{ width: 34, height: 26, display: "grid", placeItems: "center", backgroundColor: "rgba(255,255,255,0.06)" }}>
    <img src="/assets/plane.svg" alt="" style={{ width: size, height: size, filter: filterCss(filter) }} />
  </Box>
);

const rgbStringToHex = (value) => {
  const [r, g, b] = String(value).split(",").map((v) => parseInt(v, 10) || 0);
  return rgbToHex(r, g, b);
};

const RadarSettingsSidebar = ({
  selectedUnit,
  setSelectedUnit,
  showNumberMode,
  setShowNumberMode,
  speedUnit,
  setSpeedUnit,
  radarColor,
  setRadarColor,
  updateTimeout,
  setUpdateTimeout,
  trackLength,
  setTrackLength,
  showFlag,
  setShowFlag,
  enemyFilter, setEnemyFilter,
  friendlyFilter, setFriendlyFilter,
  passFilter, setPassFilter,
  unknownFilter, setUnknownFilter,
  mode = "live",        // 'live' (Radar) | 'replay' (Review)
}) => {
  const accent = accentOf(mode);
  const { auth } = useAuthContext();
  const axiosPrivate = useAxiosPrivate();

  const isAdminOrRoot = auth.roles.includes(ROLES.Admin) || auth.roles.includes(ROLES.Root)

  const { dispatch } = useUnitContext();

  const [syntheticRadius, setSyntheticRadius] = useState(3); // Default radius in kilometers

  const fetchSelectableUnits = async (userId) => {
    const response = await axiosPrivate.get('/api/units/selectable', {
      params: { userId }
    });
    dispatch({ type: 'SET_SELECTABLE_UNITS', payload: response?.data });
  };

  // Slider moves the value locally; the server is updated once, when the slider is released
  const saveSyntheticRadius = async (newRadius) => {
    if (!selectedUnit?._id) return;

    const oldRadius = selectedUnit.syntheticRadius || 3;
    const radius = newRadius || 3;
    setSyntheticRadius(radius);

    dispatch({
      type: "UPDATE_UNIT_RADIUS",
      payload: { unitId: selectedUnit._id, syntheticRadius: radius },
    });

    if (oldRadius !== radius) {
      try {
        await axiosPrivate.patch(`/api/units/${selectedUnit._id}/syntheticRadius`, { syntheticRadius: radius });
      } catch (error) {
        console.error("Error updating synthetic radius:", error);
      }
    }

    fetchSelectableUnits(auth?._id);
  };

  useEffect(() => {
    fetchSelectableUnits(auth?._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?._id, dispatch]);

  useEffect(() => {
    if (
      selectedUnit &&
      (selectedUnit.syntheticRadius === null ||
        selectedUnit.syntheticRadius === 0 ||
        typeof selectedUnit.syntheticRadius === "undefined")
    ) {
      setSyntheticRadius(3); // fallback default
    } else if (selectedUnit?.syntheticRadius) {
      setSyntheticRadius(selectedUnit.syntheticRadius);
    }
  }, [selectedUnit]);

  const foeRows = [
    { key: "enemy", label: "Enemy", value: enemyFilter, set: setEnemyFilter },
    { key: "friendly", label: "Friendly", value: friendlyFilter, set: setFriendlyFilter },
    { key: "pass", label: "Pass", value: passFilter, set: setPassFilter },
    { key: "unknown", label: "Unknown", value: unknownFilter, set: setUnknownFilter },
  ];

  return (
    <SidebarPanel accent={accent}>
      <SidebarSection icon={Label} title="Target labels" accent={accent}>
        <SegmentedSetting
          label="Number"
          value={showNumberMode}
          onChange={setShowNumberMode}
          accent={accent}
          options={[
            { value: ShowNumberModes.NONE, label: "NONE" },
            { value: ShowNumberModes.TRACK, label: "TRACK" },
            { value: ShowNumberModes.SYNTH, label: "SYNTH" },
          ]}
        />
        <SegmentedSetting
          label="Speed unit"
          value={speedUnit}
          onChange={setSpeedUnit}
          accent={accent}
          options={[
            { value: SpeedUnits.KNOTS, label: "KNOTS" },
            { value: SpeedUnits.KMH, label: "KM/H" },
            { value: SpeedUnits.MPH, label: "MPH" },
          ]}
        />
        <SwitchSetting label="Country flag" checked={showFlag} onChange={setShowFlag} accent={accent} />
      </SidebarSection>

      <SidebarSection icon={Timeline} title="Tracks & updates" accent={accent}>
        <SliderSetting
          label="Track length"
          value={trackLength}
          onChange={setTrackLength}
          min={1}
          max={50}
          accent={accent}
          format={(v) => (v >= 40 ? "All" : `${v} pts`)}
        />
        <SliderSetting
          label="Update interval"
          value={updateTimeout}
          onChange={setUpdateTimeout}
          min={3}
          max={30}
          accent={accent}
          format={(v) => `${v} s`}
        />
      </SidebarSection>

      <SidebarSection icon={RadarIcon} title="Radar" accent={accent}>
        {isAdminOrRoot && (
          <SliderSetting
            label="Synthetic radius"
            value={syntheticRadius}
            onChange={setSyntheticRadius}
            onChangeCommitted={saveSyntheticRadius}
            min={1}
            max={5}
            accent={accent}
            format={(v) => `${v} km`}
            disabled={!selectedUnit?._id}
          />
        )}
        <SwatchSetting
          label="Scan color"
          sub={`rgb(${radarColor})`}
          accent={accent}
          popupTitle="SCAN COLOR"
          swatch={<Box sx={{ width: 34, height: 20, backgroundColor: `rgb(${radarColor})` }} />}
        >
          <HexColorPicker color={rgbStringToHex(radarColor)} onChange={(hex) => setRadarColor(hexToRgbString(hex))} />
        </SwatchSetting>
      </SidebarSection>

      <SidebarSection icon={Palette} title="Target colors" accent={accent}>
        {foeRows.map(({ key, label, value, set }) => (
          <SwatchSetting
            key={key}
            label={label}
            accent={accent}
            popupTitle={`${label.toUpperCase()} COLOR`}
            swatch={<PlanePreview filter={value} />}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
              <Box sx={{ borderRadius: "8px", overflow: "hidden", border: `1px solid ${alpha(accent, 0.3)}` }}>
                <PlanePreview filter={value} size={30} />
              </Box>
              <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", maxWidth: 160 }}>
                Preview of the {label.toLowerCase()} marker on the map
              </Typography>
            </Box>
            <FilterSliders value={value} onChange={set} accent={accent} />
          </SwatchSetting>
        ))}
      </SidebarSection>
    </SidebarPanel>
  );
};

export default RadarSettingsSidebar;
